import React, { useState, useEffect } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { Spinner } from "@/components/Atoms";
import { Plus, Trash2, Save, Puzzle } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

/**
 * AppAPI Deploy-Daemon VM-size presets. Operator-defined CPU/RAM/HDD presets
 * picked when enabling AppAPI for a cloud. The full table is replaced on save
 * (PUT). IDs must be unique (a-z, 0-9, -, _).
 */
export default function AppApiVmSizesPanel() {
    const { data, mutate, isLoading } = useSWR(
        "/api/admin/infra/appapi/vm-sizes", fetcher);
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => { if (data) setRows(data.map((d) => ({ ...d }))); }, [data]);

    const update = (i, field, val) =>
        setRows(rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
    const addRow = () =>
        setRows([...rows, { id: "", label: "", cpu: 2, ram_mb: 4096, disk_gb: 40 }]);
    const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

    const save = async () => {
        setBusy(true); setErr(null); setMsg(null);
        try {
            const payload = {
                sizes: rows.map((r) => ({
                    id: (r.id || "").trim(),
                    label: (r.label || "").trim(),
                    cpu: Number(r.cpu),
                    ram_mb: Number(r.ram_mb),
                    disk_gb: Number(r.disk_gb),
                })),
            };
            const res = await api.put("/api/admin/infra/appapi/vm-sizes", payload);
            await mutate(res.data, { revalidate: false });
            setMsg(`Saved — ${res.data.length} size(s).`);
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };

    if (isLoading) return <Spinner />;

    return (
        <div className="cm-card p-5" data-testid="appapi-vm-sizes-panel">
            <div className="flex items-start gap-2 mb-3">
                <Puzzle size={18} className="text-cyan-400 mt-0.5" />
                <div>
                    <div className="cm-h3">AppAPI — VM Sizes</div>
                    <div className="text-xs text-zinc-500 mt-1 leading-relaxed max-w-2xl">
                        CPU/RAM/HDD presets for the per-cloud dedicated HaRP deploy-daemon VM
                        (Docker). When enabling <span className="text-zinc-300">AppAPI</span> on a
                        cloud, the operator picks one of these sizes. IDs must be unique
                        (a–z, 0–9, -, _).
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-[12px]" data-testid="appapi-vm-sizes-table">
                    <thead>
                        <tr className="text-zinc-500 text-[11px] uppercase tracking-wide text-left">
                            <th className="py-1.5 pr-3">ID</th>
                            <th className="py-1.5 pr-3">Label</th>
                            <th className="py-1.5 pr-3">vCPU</th>
                            <th className="py-1.5 pr-3">RAM (MB)</th>
                            <th className="py-1.5 pr-3">HDD (GB)</th>
                            <th className="py-1.5"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-t border-zinc-800" data-testid={`appapi-size-row-${i}`}>
                                <td className="py-1.5 pr-3">
                                    <input
                                        className="cm-input text-xs py-0.5 w-24"
                                        value={r.id || ""}
                                        placeholder="m"
                                        onChange={(e) => update(i, "id", e.target.value)}
                                        data-testid={`appapi-size-id-${i}`}
                                    />
                                </td>
                                <td className="py-1.5 pr-3">
                                    <input
                                        className="cm-input text-xs py-0.5 w-48"
                                        value={r.label || ""}
                                        placeholder="M — Medium"
                                        onChange={(e) => update(i, "label", e.target.value)}
                                        data-testid={`appapi-size-label-${i}`}
                                    />
                                </td>
                                <td className="py-1.5 pr-3">
                                    <input
                                        type="number" min="1" max="64"
                                        className="cm-input text-xs py-0.5 w-20"
                                        value={r.cpu}
                                        onChange={(e) => update(i, "cpu", e.target.value)}
                                        data-testid={`appapi-size-cpu-${i}`}
                                    />
                                </td>
                                <td className="py-1.5 pr-3">
                                    <input
                                        type="number" min="512" step="512"
                                        className="cm-input text-xs py-0.5 w-24"
                                        value={r.ram_mb}
                                        onChange={(e) => update(i, "ram_mb", e.target.value)}
                                        data-testid={`appapi-size-ram-${i}`}
                                    />
                                </td>
                                <td className="py-1.5 pr-3">
                                    <input
                                        type="number" min="10"
                                        className="cm-input text-xs py-0.5 w-20"
                                        value={r.disk_gb}
                                        onChange={(e) => update(i, "disk_gb", e.target.value)}
                                        data-testid={`appapi-size-disk-${i}`}
                                    />
                                </td>
                                <td className="py-1.5 text-right">
                                    <button
                                        onClick={() => removeRow(i)}
                                        className="p-1 text-red-400 hover:bg-red-950/40 rounded"
                                        title="Remove"
                                        data-testid={`appapi-size-remove-${i}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="py-3 text-zinc-500 text-xs">No sizes defined.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center gap-3 mt-4">
                <button
                    onClick={addRow}
                    className="cm-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5"
                    data-testid="appapi-size-add-btn"
                >
                    <Plus size={14} /> Add size
                </button>
                <button
                    onClick={save}
                    disabled={busy || rows.length === 0}
                    className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="appapi-size-save-btn"
                >
                    <Save size={14} /> {busy ? "Saving…" : "Save"}
                </button>
                {msg && <span className="text-xs text-emerald-400" data-testid="appapi-size-msg">{msg}</span>}
                {err && <span className="text-xs text-red-400 font-mono break-all" data-testid="appapi-size-err">{err}</span>}
            </div>
        </div>
    );
}
